import L from 'leaflet';
import 'leaflet.markercluster';
import { renderPopup } from './popup.js';

// Kuala Lumpur city centre.
const KL_CENTER = [3.139, 101.687];
const DEFAULT_ZOOM = 12;

// Colour palette keyed by lowercased place type. Unknown types fall back to a
// stable colour picked from EXTRA_COLORS so the legend stays consistent per session.
const TYPE_COLORS = {
  food: '#e8590c',
  drink: '#1971c2',
  sightseeing: '#2f9e44',
  shopping: '#9c36b5',
  nature: '#099268',
  nightlife: '#c2255c',
  cafe: '#ad6800',
  hotel: '#495057',
};
const EXTRA_COLORS = ['#5f3dc4', '#0b7285', '#a61e4d', '#5c940d', '#862e9c', '#364fc7'];
const assignedColors = new Map();

/** Stable colour for a (lowercased) type key. */
export function typeColor(type) {
  if (TYPE_COLORS[type]) return TYPE_COLORS[type];
  if (!assignedColors.has(type)) {
    assignedColors.set(type, EXTRA_COLORS[assignedColors.size % EXTRA_COLORS.length]);
  }
  return assignedColors.get(type);
}

/** Build a coloured teardrop divIcon for a marker. */
function markerIcon(type) {
  const color = typeColor(type);
  return L.divIcon({
    className: 'place-marker',
    html: `<span class="place-marker__pin" style="background:${color}"></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
    popupAnchor: [0, -20],
  });
}

/**
 * Create the map controller.
 * @param {string} elementId - id of the map container element
 */
export function createMap(elementId) {
  const map = L.map(elementId, { zoomControl: true }).setView(KL_CENTER, DEFAULT_ZOOM);

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(map);

  const cluster = L.markerClusterGroup({
    showCoverageOnHover: false,
    maxClusterRadius: 50,
  });
  map.addLayer(cluster);

  let userMarker = null;
  let userCircle = null;

  /**
   * Locate the user via the browser Geolocation API (client-side only — no
   * backend), drop a marker with an accuracy circle, and pan to it.
   * @param {{ pan?: boolean }} [opts]
   * @returns {Promise<[number, number]>} resolves with [lat, lng]
   */
  function locateUser({ pan = true } = {}) {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const latlng = [coords.latitude, coords.longitude];

          if (userMarker) userMarker.remove();
          if (userCircle) userCircle.remove();

          userCircle = L.circle(latlng, {
            radius: coords.accuracy,
            color: '#1971c2',
            weight: 1,
            fillOpacity: 0.12,
          }).addTo(map);

          userMarker = L.marker(latlng, {
            icon: L.divIcon({
              className: 'user-marker',
              html: '<span class="user-marker__dot"></span>',
              iconSize: [18, 18],
              iconAnchor: [9, 9],
            }),
            title: 'You are here',
            zIndexOffset: 1000,
          }).addTo(map);

          if (pan) map.setView(latlng, 15);
          resolve(latlng);
        },
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
      );
    });
  }

  /**
   * Draw the given places, replacing any existing markers, and fit the view to them.
   * @param {import('./data.js').Place[]} places
   */
  function render(places) {
    cluster.clearLayers();

    const markers = places.map((place) =>
      L.marker([place.lat, place.lng], {
        icon: markerIcon(place.type),
        title: place.name,
      }).bindPopup(renderPopup(place), {
        maxWidth: Math.min(280, window.innerWidth - 40),
      }),
    );

    cluster.addLayers(markers);

    if (markers.length > 0) {
      const bounds = L.latLngBounds(places.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }

  return { map, render, locateUser };
}
